// =============================================================================
// Batch Schema - Tracks multi-file upload batch processing
// =============================================================================
// v1.0 - Initial implementation for Infrastructure-Throttled Fan-Out pattern
// =============================================================================
import { z } from 'zod';
// =============================================================================
// Constants
// =============================================================================
/** Maximum number of files allowed per batch */
export const MAX_BATCH_SIZE = 100;
/** Maximum file size in bytes (50MB) */
export const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;
// =============================================================================
// Batch Status Enum
// =============================================================================
/**
 * Batch processing status lifecycle:
 * PENDING → PROCESSING → SYNTHESIZING → COMPLETED/PARTIAL_FAILURE/FAILED
 *
 * v5.0: AGGREGATING is deprecated, replaced by SYNTHESIZING for property-level insight generation
 */
export const BatchStatusEnum = z.enum([
    'PENDING', // Batch created, jobs being queued
    'PROCESSING', // Jobs are being processed by workers
    'AGGREGATING', // DEPRECATED: Kept for backward compatibility
    'SYNTHESIZING', // v5.0: All docs done, property insights synthesis in progress
    'COMPLETED', // All done successfully
    'PARTIAL_FAILURE', // Some documents failed, synthesis completed
    'FAILED', // All documents failed or critical error
]);
// =============================================================================
// Document Status in Batch (for progress tracking)
// =============================================================================
export const BatchDocumentStatusEnum = z.enum([
    'PENDING', // Not yet queued
    'QUEUED', // Queued for processing
    'PROCESSING', // Being processed by worker
    'READY', // Successfully processed
    'ERROR', // Failed to process
]);
// =============================================================================
// Batch Error Schema (for tracking individual document failures)
// =============================================================================
export const BatchErrorSchema = z.object({
    documentId: z.string(),
    fileName: z.string(),
    errorMessage: z.string(),
    errorCode: z.string().optional(),
    occurredAt: z.string().datetime().optional(),
});
// =============================================================================
// Batch Document Schema (for API responses)
// =============================================================================
export const BatchResponseSchema = z.object({
    _id: z.string(),
    organizationId: z.string(),
    propertyId: z.string(),
    // Document tracking
    totalDocuments: z.number().int().min(1).max(MAX_BATCH_SIZE),
    processedCount: z.number().int().min(0), // success + error
    successCount: z.number().int().min(0),
    errorCount: z.number().int().min(0),
    // Status
    status: BatchStatusEnum,
    // Timestamps
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    completedAt: z.string().datetime().nullable(),
    // Error details (if any documents failed)
    errors: z.array(BatchErrorSchema).optional(),
    // S3 keys for aggregated files (after aggregation completes)
    propertyContextS3Key: z.string().optional(),
    propertyAnalysisS3Key: z.string().optional(),
});
// =============================================================================
// Batch Progress Schema (for polling endpoint)
// =============================================================================
export const BatchDocumentProgressSchema = z.object({
    documentId: z.string(),
    fileName: z.string(),
    status: BatchDocumentStatusEnum,
    errorMessage: z.string().optional(),
});
export const BatchProgressSchema = z.object({
    batchId: z.string(),
    status: BatchStatusEnum,
    totalDocuments: z.number().int(),
    processedCount: z.number().int(),
    successCount: z.number().int(),
    errorCount: z.number().int(),
    percentComplete: z.number().min(0).max(100),
    // Per-document status for detailed UI
    documents: z.array(BatchDocumentProgressSchema),
    // Timing
    createdAt: z.string().datetime(),
    estimatedSecondsRemaining: z.number().nullable(),
});
// =============================================================================
// API Request/Response Schemas
// =============================================================================
// -----------------------------------------------------------------------------
// POST /batches/presign - Get presigned URLs for batch upload
// -----------------------------------------------------------------------------
export const BatchPresignFileSchema = z.object({
    fileName: z.string().min(1).max(255),
    mimeType: z.string().default('application/pdf'),
    sizeBytes: z.number().int().positive().max(MAX_FILE_SIZE_BYTES),
});
export const BatchPresignRequestSchema = z.object({
    propertyId: z.string().min(1),
    files: z.array(BatchPresignFileSchema).min(1).max(MAX_BATCH_SIZE),
});
export const BatchPresignUploadSchema = z.object({
    fileName: z.string(),
    uploadUrl: z.string().url(),
    s3Key: z.string(),
    expiresIn: z.number().int().positive(),
});
export const BatchPresignResponseSchema = z.object({
    uploads: z.array(BatchPresignUploadSchema),
});
// -----------------------------------------------------------------------------
// POST /batches/process - Create batch and start processing
// -----------------------------------------------------------------------------
export const BatchProcessDocumentSchema = z.object({
    title: z.string().min(1).max(500),
    fileName: z.string().min(1).max(255),
    s3Key: z.string().min(1),
    mimeType: z.string().default('application/pdf'),
    sizeBytes: z.number().int().positive(),
});
export const BatchProcessRequestSchema = z.object({
    propertyId: z.string().min(1),
    documents: z.array(BatchProcessDocumentSchema).min(1).max(MAX_BATCH_SIZE),
});
export const BatchProcessDocumentResultSchema = z.object({
    documentId: z.string(),
    fileName: z.string(),
    status: z.string(),
});
export const BatchProcessResponseSchema = z.object({
    batchId: z.string(),
    totalDocuments: z.number().int(),
    status: BatchStatusEnum,
    documents: z.array(BatchProcessDocumentResultSchema),
});
/**
 * Validate batch files before upload
 *
 * @param files - Array of files with fileName, sizeBytes, and optionally mimeType
 * @returns Validation result with errors if any
 */
export function validateBatchFiles(files) {
    const errors = [];
    // Check batch size
    if (files.length === 0) {
        return {
            valid: false,
            errors: [{ fileName: '', error: 'At least one file is required' }],
        };
    }
    if (files.length > MAX_BATCH_SIZE) {
        return {
            valid: false,
            errors: [{ fileName: '', error: `Maximum ${MAX_BATCH_SIZE} files allowed per batch` }],
        };
    }
    // Check each file
    for (const file of files) {
        // Check file extension
        const ext = file.fileName.toLowerCase().split('.').pop();
        if (ext !== 'pdf') {
            errors.push({
                fileName: file.fileName,
                error: 'Only PDF files are allowed',
            });
            continue;
        }
        // Check file size
        if (file.sizeBytes > MAX_FILE_SIZE_BYTES) {
            const maxMB = MAX_FILE_SIZE_BYTES / 1024 / 1024;
            errors.push({
                fileName: file.fileName,
                error: `File exceeds maximum size of ${maxMB}MB`,
            });
        }
        // Check file size is positive
        if (file.sizeBytes <= 0) {
            errors.push({
                fileName: file.fileName,
                error: 'File size must be greater than 0',
            });
        }
        // Check mime type if provided
        if (file.mimeType && !file.mimeType.includes('pdf')) {
            errors.push({
                fileName: file.fileName,
                error: 'Invalid MIME type, must be application/pdf',
            });
        }
    }
    return {
        valid: errors.length === 0,
        errors,
    };
}
/**
 * Get human-readable validation error message
 */
export function formatBatchValidationErrors(result) {
    if (result.valid) {
        return '';
    }
    return result.errors
        .map(e => e.fileName ? `${e.fileName}: ${e.error}` : e.error)
        .join('; ');
}
//# sourceMappingURL=batch.js.map